import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Badge } from '../../components/ui/badge';
import { HelpCircle, Search, Building2, CreditCard, FileText, Users, Shield, Bell, MessageSquare } from 'lucide-react';

const FAQ_CATEGORIES = {
  general: {
    name: 'General',
    icon: HelpCircle,
    color: 'bg-blue-500',
  },
  properties: {
    name: 'Properties & Units',
    icon: Building2,
    color: 'bg-emerald-500',
  },
  payments: {
    name: 'Payments',
    icon: CreditCard,
    color: 'bg-amber-500',
  },
  leases: {
    name: 'Leases',
    icon: FileText,
    color: 'bg-purple-500',
  },
  tenants: {
    name: 'Tenants',
    icon: Users,
    color: 'bg-pink-500',
  },
  security: {
    name: 'Account & Security',
    icon: Shield,
    color: 'bg-red-500',
  },
  notifications: {
    name: 'Notifications',
    icon: Bell,
    color: 'bg-orange-500',
  },
  messaging: {
    name: 'Messaging',
    icon: MessageSquare,
    color: 'bg-cyan-500',
  },
  subscription: {
    name: 'Subscription & Billing',
    icon: CreditCard,
    color: 'bg-indigo-500',
  },
};

const FAQS = [
  // General
  {
    category: 'general',
    question: 'What is U-DORM?',
    answer: 'U-DORM is a comprehensive dormitory management system that helps landlords manage their properties, units, tenants, leases, and payments. It provides separate portals for administrators, landlords, property managers, and tenants.',
    roles: ['all'],
  },
  {
    category: 'general',
    question: 'How do I get started?',
    answer: 'If you\'re a landlord, register for an account and you\'ll be guided through a quick 4-question setup, then prompted to pick a subscription (Monthly $9.99 or Annual $95) — backed by our 30-day money-back guarantee. Once subscribed, add your properties, create units, and invite tenants. Tenants can register using your unique Landlord Code or a direct invite link.',
    roles: ['all'],
  },
  {
    category: 'general',
    question: 'What is a Landlord Code?',
    answer: 'A Landlord Code is a unique 6-character code assigned to each verified landlord. Tenants can use this code during registration to associate themselves with a specific landlord, instead of selecting from a dropdown list.',
    roles: ['all'],
  },
  
  // Properties & Units
  {
    category: 'properties',
    question: 'How do I add a new property?',
    answer: 'Go to the Properties page in your dashboard and click "Add Property". Fill in the property name, address, and other details. After creating a property, you can add individual units to it.',
    roles: ['landlord', 'property_manager'],
  },
  {
    category: 'properties',
    question: 'Can I create multiple units at once?',
    answer: 'Yes! When adding units to a property, you can use the "Bulk Create" option to create multiple units with sequential numbers. This is useful when setting up a new property with many rooms.',
    roles: ['landlord', 'property_manager'],
  },
  {
    category: 'properties',
    question: 'What do the unit statuses mean?',
    answer: 'Units can have three statuses: "Vacant" (available for lease), "Occupied" (currently leased to a tenant), and "Maintenance" (temporarily unavailable). The status automatically updates when leases are created or terminated.',
    roles: ['landlord', 'property_manager'],
  },
  
  // Payments
  {
    category: 'payments',
    question: 'How do tenants submit payments?',
    answer: 'Tenants go to the Payments page in their dashboard, select the payment type (rent, utilities, etc.), enter the amount, upload proof of payment (receipt/screenshot), and submit. The landlord will then verify the payment.',
    roles: ['all'],
  },
  {
    category: 'payments',
    question: 'How do I verify a payment?',
    answer: 'When a tenant submits a payment, you\'ll receive a notification. Go to the Payments page, review the payment details and proof, then click "Verify" to confirm or "Reject" if there\'s an issue.',
    roles: ['landlord', 'property_manager'],
  },
  {
    category: 'payments',
    question: 'How do I get a receipt for my payment?',
    answer: 'After your payment is verified by the landlord, a receipt will be automatically generated. Go to the Receipts page in your dashboard to view and download PDF receipts for all verified payments.',
    roles: ['tenant'],
  },
  {
    category: 'payments',
    question: 'Can I set up payment methods for tenants?',
    answer: 'Yes! Go to Settings > Payment Methods to add your accepted payment methods (bank accounts, e-wallets, etc.). These will be displayed to tenants when they submit payments.',
    roles: ['landlord'],
  },
  
  // Leases
  {
    category: 'leases',
    question: 'How do I create a lease agreement?',
    answer: 'Go to the Leases page and click "Create Lease". Select a vacant unit, choose an available tenant, set the lease dates, rent amount, and any additional fees. The tenant will be notified to acknowledge the lease.',
    roles: ['landlord', 'property_manager'],
  },
  {
    category: 'leases',
    question: 'What happens after I create a lease?',
    answer: 'After creating a lease, it\'s in "Pending" status. The tenant must acknowledge the lease terms. Once acknowledged, you can activate the lease to make it "Active". The unit status will automatically change to "Occupied".',
    roles: ['landlord', 'property_manager'],
  },
  {
    category: 'leases',
    question: 'Can I edit a lease after it\'s created?',
    answer: 'You can edit pending leases before they\'re acknowledged. Once a tenant acknowledges a lease, it becomes locked. To make changes to an active lease, you\'ll need to terminate it and create a new one.',
    roles: ['landlord', 'property_manager'],
  },
  {
    category: 'leases',
    question: 'How do I acknowledge my lease?',
    answer: 'When your landlord creates a lease for you, you\'ll receive a notification. Go to the Lease page, review all the terms including house rules, payment terms, and conditions. Click "Acknowledge" to confirm you accept the terms.',
    roles: ['tenant'],
  },
  {
    category: 'leases',
    question: 'Can I delete a lease?',
    answer: 'Yes, you can delete pending leases that haven\'t been acknowledged yet. For active leases, you should use the "Terminate" option instead, which preserves the payment history and properly releases the unit.',
    roles: ['landlord', 'property_manager'],
  },
  
  // Tenants
  {
    category: 'tenants',
    question: 'How do tenants register for my property?',
    answer: 'Tenants can register using your Landlord Code (shown in your profile) or by selecting you from the landlord list during registration. After registration, you\'ll need to verify them before they can access their dashboard.',
    roles: ['landlord'],
  },
  {
    category: 'tenants',
    question: 'What does verifying a tenant mean?',
    answer: 'When a tenant registers with your code, they appear in your "Pending Verification" list. Verifying confirms they\'re legitimate tenants. You\'ll then be redirected to create a lease for them.',
    roles: ['landlord'],
  },
  {
    category: 'tenants',
    question: 'Can I remove a tenant?',
    answer: 'You can remove tenants who don\'t have active leases. Go to the Tenants page, find them in the "Available" or "Former Tenants" tab, and click "Remove". This deactivates their account but preserves payment history.',
    roles: ['landlord'],
  },
  {
    category: 'tenants',
    question: 'How do I offboard a tenant who is moving out?',
    answer: 'On the Tenants page, open the tenant\'s details panel and click "Offboard Tenant". You\'ll be guided through terminating the lease, deciding how to handle their deposit (returned vs. forfeited with reason), and recording the move-out date. A move-out PDF statement is automatically generated and emailed to the tenant for their records, and their account is deactivated.',
    roles: ['landlord'],
  },
  {
    category: 'tenants',
    question: 'Can I re-onboard a former tenant?',
    answer: 'Yes! Open the Tenants page, find the tenant under "History", and click "Re-onboard". This reactivates their account, links them back to you, and sends a welcome-back email. From there you can immediately create a new lease.',
    roles: ['landlord'],
  },
  {
    category: 'tenants',
    question: 'What is a Property Manager and what can they do?',
    answer: 'A Property Manager is a user you invite to help run your dorm. You can grant fine-grained permissions per manager: view/edit properties, view/verify payments, invite tenants, manage expenses, send messages, offboard tenants, and more. They access a dedicated Manager portal that mirrors the Landlord features they\'re permitted for.',
    roles: ['landlord'],
  },
  {
    category: 'tenants',
    question: 'How do I invite a Property Manager?',
    answer: 'Go to the Managers page, click "Invite Manager", enter their name and email, choose which permissions to grant (e.g., Verify Payments, Invite Tenants, Send Messages), and we\'ll email them a secure invite link to set up their account.',
    roles: ['landlord'],
  },
  
  // Account & Security
  {
    category: 'security',
    question: 'How do I change my password?',
    answer: 'Go to your Profile page and click on the security settings. You can change your password by entering your current password and setting a new one. Make sure to use a strong password.',
    roles: ['all'],
  },
  {
    category: 'security',
    question: 'I forgot my password. How do I reset it?',
    answer: 'On the login page, click "Forgot Password" and enter your email address. You\'ll receive a password reset link via email. Click the link and set a new password.',
    roles: ['all'],
  },
  {
    category: 'security',
    question: 'How do I update my profile information?',
    answer: 'Go to your Profile page from the sidebar menu. You can update your name, phone number, and profile picture. Some information like email cannot be changed after registration.',
    roles: ['all'],
  },
  
  // Notifications
  {
    category: 'notifications',
    question: 'What types of notifications will I receive?',
    answer: 'You\'ll receive notifications for important events like new tenant registrations, payment submissions, lease acknowledgments, and messages. Notifications appear in the bell icon at the top of the page.',
    roles: ['all'],
  },
  {
    category: 'notifications',
    question: 'How do I manage my notification settings?',
    answer: 'Go to Settings and look for the Notifications section. You can configure which types of notifications you want to receive and how you want to be notified.',
    roles: ['landlord'],
  },
  
  // Messaging
  {
    category: 'messaging',
    question: 'How do I send a message to my tenant/landlord?',
    answer: 'Go to the Messages page and select your contact from the list. Type your message and click send. You can also attach images and documents to your messages.',
    roles: ['all'],
  },
  {
    category: 'messaging',
    question: 'What file types can I attach to messages?',
    answer: 'You can attach images (JPG, PNG, GIF, WebP - max 5MB), documents (PDF, DOC, DOCX - max 10MB), and spreadsheets (XLS, XLSX - max 10MB) to your messages.',
    roles: ['all'],
  },
];

export default function FAQsPage() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Filter FAQs based on user role, search query, and category
  const filteredFAQs = FAQS.filter(faq => {
    // Filter by role
    const roleMatch = faq.roles.includes('all') || faq.roles.includes(user?.role);
    
    // Filter by category
    const categoryMatch = selectedCategory === 'all' || faq.category === selectedCategory;
    
    // Filter by search
    const searchMatch = !searchQuery || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    
    return roleMatch && categoryMatch && searchMatch;
  });

  // Group FAQs by category
  const groupedFAQs = filteredFAQs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="faqs-page">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold flex items-center gap-2">
          <HelpCircle className="w-8 h-8 text-primary" />
          Frequently Asked Questions
        </h1>
        <p className="text-muted-foreground mt-1">Find answers to common questions about U-DORM</p>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="faq-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setSelectedCategory('all')}
              >
                All
              </Badge>
              {Object.entries(FAQ_CATEGORIES).map(([key, cat]) => (
                <Badge
                  key={key}
                  variant={selectedCategory === key ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setSelectedCategory(key)}
                >
                  <cat.icon className="w-3 h-3 mr-1" />
                  {cat.name}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FAQs List */}
      {filteredFAQs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <HelpCircle className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No FAQs Found</h3>
            <p className="text-muted-foreground text-center max-w-md">
              Try adjusting your search or filters to find what you're looking for.
            </p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedFAQs).map(([category, faqs]) => {
          const categoryInfo = FAQ_CATEGORIES[category];
          const Icon = categoryInfo.icon;
          
          return (
            <Card key={category}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${categoryInfo.color} text-white`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  {categoryInfo.name}
                </CardTitle>
                <CardDescription>{faqs.length} question(s)</CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq) => (
                    <AccordionItem key={`${category}-${faq.question}`} value={`${category}-${faq.question}`}>
                      <AccordionTrigger className="text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}
